import { defineStore } from 'pinia'
import categoryEn from '@/data/category-en.json'
import { fetchData } from '@/utils/fetch'
import storage from '@/utils/storage'
import { useValidCache } from '@/hooks/useValidCache'
import {
  CATEGORY_EN_LANG,
  buildCategoryNameMap,
  cloneCategoryTree,
  mergeCategoryNames,
  pickFirstLevelCategories
} from '@/utils/categoryMerge'

const categoryEnTree = cloneCategoryTree(categoryEn)

function getSubordinateFromEn (firstCategoryId) {
  const item = categoryEnTree.find(
    category => String(category.categoryId) === String(firstCategoryId)
  )
  return item?.childCategoryList ? cloneCategoryTree(item.childCategoryList) : []
}

function getCachedData (cacheKey) {
  const { cacheData, CACHE_VERSION } = useValidCache(cacheKey)
  if (!cacheData.value?.data) return null

  return {
    data: cacheData.value.data,
    CACHE_VERSION
  }
}

function setCachedData (cacheKey, data, CACHE_VERSION) {
  storage.set(cacheKey, {
    data,
    timestamp: Date.now(),
    cache_version: CACHE_VERSION
  })
}

export const useCategoryStore = defineStore('bestdealer-category', () => {
  const firstLevel = ref(pickFirstLevelCategories(categoryEnTree))
  const allCategory = ref(cloneCategoryTree(categoryEnTree))
  const localizedLanguage = ref(CATEGORY_EN_LANG)
  const loading = ref(false)

  let pendingLanguage = null
  let loadForLanguageTask = null
  let loadFirstLevelTask = null

  const applyLocalizedTree = (language, tree) => {
    firstLevel.value = pickFirstLevelCategories(tree)
    allCategory.value = cloneCategoryTree(tree)
    localizedLanguage.value = language
  }

  const applyLocalizedFirstLevel = (language, list) => {
    firstLevel.value = cloneCategoryTree(list)
    localizedLanguage.value = language
  }

  const loadForLanguage = async (language) => {
    applyLocalizedTree(CATEGORY_EN_LANG, categoryEnTree)

    if (language === CATEGORY_EN_LANG) {
      pendingLanguage = null
      loadForLanguageTask = null
      return
    }

    if (loadForLanguageTask && pendingLanguage === language) {
      return loadForLanguageTask
    }

    pendingLanguage = language

    loadForLanguageTask = (async () => {
      const allCacheKey = `cacheAllCategory_${language}`
      const cachedAll = getCachedData(allCacheKey)
      if (cachedAll) {
        if (pendingLanguage === language) {
          applyLocalizedTree(language, cachedAll.data)
        }
        return
      }

      loading.value = true
      try {
        const { code, data } = await fetchData(
          `/dsp-app/alibaba-common/allCategory?language=${language}`,
          { method: 'get' }
        )

        if (code !== 200 || pendingLanguage !== language) return

        const merged = mergeCategoryNames(
          categoryEnTree,
          buildCategoryNameMap(data)
        )
        const { CACHE_VERSION } = useValidCache(allCacheKey)
        setCachedData(allCacheKey, merged, CACHE_VERSION)

        const firstCacheKey = `cacheShopCategory_${language}`
        setCachedData(
          firstCacheKey,
          pickFirstLevelCategories(merged),
          CACHE_VERSION
        )

        applyLocalizedTree(language, merged)
      } finally {
        if (pendingLanguage === language) {
          loading.value = false
          loadForLanguageTask = null
        }
      }
    })()

    return loadForLanguageTask
  }

  const loadFirstLevel = async (language) => {
    applyLocalizedFirstLevel(CATEGORY_EN_LANG, pickFirstLevelCategories(categoryEnTree))

    if (language === CATEGORY_EN_LANG) {
      pendingLanguage = null
      loadFirstLevelTask = null
      return firstLevel.value
    }

    if (localizedLanguage.value === language && allCategory.value.length) {
      applyLocalizedFirstLevel(language, pickFirstLevelCategories(allCategory.value))
      return firstLevel.value
    }

    const allCacheKey = `cacheAllCategory_${language}`
    const cachedAll = getCachedData(allCacheKey)
    if (cachedAll) {
      applyLocalizedTree(language, cachedAll.data)
      return firstLevel.value
    }

    const firstCacheKey = `cacheShopCategory_${language}`
    const cachedFirst = getCachedData(firstCacheKey)
    if (cachedFirst) {
      applyLocalizedFirstLevel(language, cachedFirst.data)
      return firstLevel.value
    }

    if (loadFirstLevelTask && pendingLanguage === language) {
      return loadFirstLevelTask
    }

    pendingLanguage = language

    loadFirstLevelTask = fetchData(
      `/dsp-app/alibaba-common/firstLevelCategory?language=${language}`,
      { method: 'get' }
    ).then(({ code, data }) => {
      if (code !== 200 || pendingLanguage !== language) return firstLevel.value

      const nameMap = buildCategoryNameMap(data)
      const mergedFirstLevel = pickFirstLevelCategories(categoryEnTree).map(item => ({
        ...item,
        categoryName: nameMap.get(String(item.categoryId)) ?? item.categoryName
      }))

      const { CACHE_VERSION } = useValidCache(firstCacheKey)
      setCachedData(firstCacheKey, mergedFirstLevel, CACHE_VERSION)
      applyLocalizedFirstLevel(language, mergedFirstLevel)
      return firstLevel.value
    }).finally(() => {
      if (pendingLanguage === language) {
        loadFirstLevelTask = null
      }
    })

    return loadFirstLevelTask
  }

  const getSubordinateCategories = async (language, firstCategoryId) => {
    const enData = getSubordinateFromEn(firstCategoryId)

    if (language === CATEGORY_EN_LANG) {
      return enData
    }

    if (localizedLanguage.value === language) {
      const firstCategory = allCategory.value.find(
        category => String(category.categoryId) === String(firstCategoryId)
      )
      if (firstCategory?.childCategoryList?.length) {
        return cloneCategoryTree(firstCategory.childCategoryList)
      }
    }

    const subCacheKey = `cacheSubCategory_${language}_${firstCategoryId}`
    const cachedSub = getCachedData(subCacheKey)
    if (cachedSub) {
      return cloneCategoryTree(cachedSub.data)
    }

    const { code, data } = await fetchData(
      `/dsp-app/alibaba-common/subordinateCategory?language=${language}&firstCategoryId=${firstCategoryId}`
    )

    if (code !== 200) {
      return enData
    }

    const merged = mergeCategoryNames(enData, buildCategoryNameMap(data))
    const { CACHE_VERSION } = useValidCache(subCacheKey)
    setCachedData(subCacheKey, merged, CACHE_VERSION)
    return merged
  }

  return {
    firstLevel,
    allCategory,
    localizedLanguage,
    loading,
    loadForLanguage,
    loadFirstLevel,
    getSubordinateCategories
  }
})
